(function () {
  "use strict";
  const productCache = Object.create(null);

  const $all = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (m) => ({
    "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#039;"
  }[m]));

  const chipClass = (t) => {
    t = String(t || "").trim();
    if (!t || t === "—") return "flat";
    const v = Number(t.replace("%","").replace(",",".").replace("−","-"));
    if (!isFinite(v)) return "flat";
    if (v > 0) return "up";
    if (v < 0) return "down";
    return "flat";
  };
  const chip = (t) => `<span class="tcgix-chip ${chipClass(t)}">${esc(t || "—")}</span>`;

  const parseHandle = (href) => {
    try {
      const u = href.startsWith("http") ? new URL(href) : new URL(href, window.location.origin);
      const p = u.pathname || "";
      const idx = p.indexOf("/products/");
      if (idx === -1) return "";
      return (p.slice(idx + "/products/".length).split("/")[0] || "").trim();
    } catch { return ""; }
  };

  const withWidth = (src) => {
    if (!src) return "";
    return src.includes("width=") ? src : (src + (src.includes("?") ? "&" : "?") + "width=60");
  };

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    return productCache[handle];
  }

  async function hydrateImages(block) {
    const imgs = $all('img.tcgix-constituent-img[data-handle]', block);
    if (!imgs.length) return;

    const q = imgs.slice();
    const CONC = 6;

    const pick = (p) => withWidth(p?.featured_image || p?.images?.[0] || "");

    const workers = [];
    for (let i = 0; i < CONC; i++) {
      workers.push((async () => {
        while (q.length) {
          const img = q.shift();
          const handle = img.getAttribute("data-handle") || "";
          if (!handle) continue;
          const prod = await fetchProduct(handle);
          const src = pick(prod);
          if (src) img.src = src;
        }
      })());
    }
    await Promise.all(workers);
  }

  function buildRow(r, hasImages) {
    // [title, img, href, cur, day, week, month, ytd, low, high]
    const [title, img, href, cur, day, week, month, ytd, low, high] = r;

    let imgHtml = "";
    if (hasImages) {
      if (img) {
        imgHtml = `<img class="tcgix-constituent-img" alt="${esc(title)}" loading="lazy" src="${esc(withWidth(img))}" width="30" height="42">`;
      } else {
        const handle = parseHandle(href || "");
        const ph = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
        imgHtml = `<img class="tcgix-constituent-img" alt="${esc(title)}" loading="lazy" src="${ph}" width="30" height="42" data-handle="${esc(handle)}">`;
      }
    }

    return `
      <tr class="tcgix-row" data-href="${esc(href || "#")}" tabindex="0">
        <td>
          <div class="tcgix-constituent">
            ${imgHtml}
            <div class="tcgix-strong">${esc(title || "")}</div>
          </div>
        </td>
        <td class="num">${esc(cur || "—")}</td>
        <td class="num">${chip(day)}</td>
        <td class="num">${chip(week)}</td>
        <td class="num">${chip(month)}</td>
        <td class="num">${chip(ytd)}</td>
        <td class="num">${esc(low || "—")}</td>
        <td class="num">${esc(high || "—")}</td>
        <td class="num"><span class="tcgix-view">View →</span></td>
      </tr>`;
  }

  function wireClicks(root) {
    const go = (href) => {
      if (!href) return;
      if (href.startsWith("http")) window.open(href, "_blank");
      else window.location.href = href;
    };
    $all("tr.tcgix-row", root).forEach(tr => {
      tr.addEventListener("click", () => go(tr.dataset.href));
      tr.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(tr.dataset.href); }
      });
    });
  }

  function render(block) {
    const dataEl = block.querySelector("script.tcgix-data");
    const host = block.querySelector("[data-tcgix-render]");
    if (!dataEl || !host) return;

    let payload;
    try { payload = JSON.parse(dataEl.textContent || "{}"); } catch { return; }

    const hasImages = !!payload.has_images;
    const groups = payload.groups || [];

    const colgroup = `
      <colgroup>
        <col class="c-product">
        <col class="c-current">
        <col class="c-day">
        <col class="c-week">
        <col class="c-month">
        <col class="c-ytd">
        <col class="c-low">
        <col class="c-high">
        <col class="c-view">
      </colgroup>`;

    let html = "";
    for (let g = 0; g < groups.length; g++) {
      const [name, rows] = groups[g];
      html += `<h3 class="tcgix-group__title${g ? " tcgix-group__title--spaced" : ""}">${esc(name || "")}</h3>`;
      html += `<div class="tcgix-tablewrap" role="region" aria-label="Constituents table" tabindex="0">
        <table class="tcgix-table" data-sort-table="1">
          ${colgroup}
          <thead><tr>
            <th data-sort="text">Product</th>
            <th class="num" data-sort="number">Current</th>
            <th class="num" data-sort="percent">Δ Day</th>
            <th class="num" data-sort="percent">Δ Week</th>
            <th class="num" data-sort="percent">Δ Month</th>
            <th class="num" data-sort="percent">Δ YTD</th>
            <th class="num" data-sort="number">52W Low</th>
            <th class="num" data-sort="number">52W High</th>
            <th class="num">View</th>
          </tr></thead>
          <tbody>${(rows || []).map(r => buildRow(r, hasImages)).join("")}</tbody>
        </table>
      </div>`;
    }

    host.innerHTML = html;
    wireClicks(block);
    if (hasImages) hydrateImages(block);
  }

  function boot() { $all(".tcgix").forEach(render); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();